import React, { FunctionComponent, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-location";

import { HashLink } from "../../navigation/HashLink";
import { useHunterTireSetting } from "../../stores/hooks/useHunterTireSetting";
import { useUrl } from "../../stores/hooks/useUrl";
import { HashtagString } from "../../stores/useHashState";
import { Dialog } from "../../ui/Dialog/Dialog";
import { DialogHeader } from "../../ui/Dialog/DialogHeader";
import { TabButton } from "../../ui/Tabs/TabButton";
import { TabsGroup } from "../../ui/Tabs/TabsGroup";
import { DealerOrdersTable } from "./DealerOrdersTable";
import { HunterTireTable } from "./HunterTireTable";
import { LeadsTable } from "./LeadsTable";
import { QuotesTable } from "./QuotesTable";
import { QuoteFilters } from "./filters/QuoteFilters";

export const myLeadsHash: HashtagString = "#my-leads";
export const myOrdersHash: HashtagString = "#my-orders";
export const myQuotesHash: HashtagString = "#my-quotes";
export const hunterTireHash: HashtagString = "#hunter-tire";

const supportedHashes = new Set<string>([
  myOrdersHash,
  myLeadsHash,
  myQuotesHash,
  hunterTireHash,
]);

const QuotesDialog: FunctionComponent = () => {
  const { t } = useTranslation();
  const { hash, pathname } = useUrl();
  const visible = supportedHashes.has(hash);
  const handleClose = useCallback(() => {
    navigate(pathname);
  }, [pathname]);
  const isLeads = hash === myLeadsHash;
  const isQuotes = hash === myQuotesHash;
  const isDealerOrders = hash === myOrdersHash;
  const shouldExposeHunterTire = useHunterTireSetting();
  const isHunterTire = hash === hunterTireHash;

  return (
    <Dialog
      className="w-full"
      id="quotes-dialog"
      onClose={handleClose}
      placement="top"
      visible={visible}
    >
      <DialogHeader
        title={
          <>
            <TabsGroup className="sm:mr-2 md:max-w-md" id="order-view-tabs">
              <HashLink hash={myLeadsHash}>
                <TabButton
                  aria-label="myLeadsTabButton"
                  className="flex-1 text-sm"
                  element="a"
                  selected={isLeads}
                >
                  {t("auth.myLeads")}
                </TabButton>
              </HashLink>
              <HashLink hash={myQuotesHash}>
                <TabButton
                  aria-label="myQuotesTabButton"
                  className="flex-1 text-sm"
                  element="a"
                  selected={isQuotes}
                >
                  {t("auth.myQuotes")}
                </TabButton>
              </HashLink>
              <HashLink hash={myOrdersHash}>
                <TabButton
                  aria-label="myOrdersTabButton"
                  className="flex-1 text-sm"
                  element="a"
                  selected={isDealerOrders}
                >
                  {t("auth.myOrders")}
                </TabButton>
              </HashLink>
              {shouldExposeHunterTire && (
                <HashLink hash={hunterTireHash}>
                  <TabButton
                    aria-label="hunterTireTabButton"
                    className="flex-1 text-sm"
                    element="a"
                    selected={isHunterTire}
                  >
                    {t("auth.hunterTire")}
                  </TabButton>
                </HashLink>
              )}
            </TabsGroup>
          </>
        }
        onClose={handleClose}
      />
      <QuoteFilters />
      <section className="p-4 md:p-6 overflow-x-auto w-full">
        {isLeads && <LeadsTable />}
        {isQuotes && <QuotesTable />}
        {isDealerOrders && <DealerOrdersTable />}
        {isHunterTire && <HunterTireTable />}
      </section>
    </Dialog>
  );
};

export default memo(QuotesDialog);
